'use client';

import { useMemo, useState } from 'react';
import { CheckCheck } from 'lucide-react';

import type {
  CareStateRequestStatusT,
  CareStateRequestT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { useCareStateDomain } from '@/entities/care-state-domain';
import { useUserInfoQuery } from '@/entities/user-info';
import { AgentTweakableArea } from '@/features/copilot/agent-tweakable';
import { useCopilotComposer } from '@/features/copilot/lib';
import { CortexPromptStrip } from '@/features/cortex-prompts';
import {
  ProposedActionList,
  useProposedActionsPlan,
  type EnrichedProposedAction,
} from '@/features/proposed-actions-plan';
import { Card } from '@/shared/ui/primitives';
import { Button } from '@/shared/ui/shadcn/button';
import { Tabs, TabsList, TabsTrigger } from '@/shared/ui/shadcn/tabs';

import { groupActionsByRequest } from '../model/groupActionsByRequest';
import { WRAP_UP_CHART_PROMPT, WRAP_UP_CHART_PROMPT_SOURCE } from '../model/wrapUpChartPrompt';
import { RequestListCard } from './RequestListCard';

type RequestFilter = 'unresolved' | 'resolved';

const UNRESOLVED_STATUSES = new Set<CareStateRequestStatusT>(['Open', 'Pending Follow Up']);

function isUnresolved(request: CareStateRequestT): boolean {
  return UNRESOLVED_STATUSES.has(request.status);
}

function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? `${count} ${singular}` : `${count} ${plural}`;
}

function firstName(fullName: string): string {
  const trimmed = fullName.trim();
  if (trimmed.length === 0) return '';
  const [first = ''] = trimmed.split(/\s+/);
  return first;
}

export interface RequestsListTabProps {
  userId: string;
}

export function RequestsListTab({ userId }: RequestsListTabProps) {
  const { careState } = useCareStateDomain();
  const userInfoQuery = useUserInfoQuery(userId);
  const { actions } = useProposedActionsPlan();
  const { sendText } = useCopilotComposer();
  const [filter, setFilter] = useState<RequestFilter>('unresolved');

  const patientFirstName = firstName(userInfoQuery.data?.patientInfo?.name ?? '');
  const displayName = patientFirstName.length > 0 ? patientFirstName : 'This patient';

  const { unresolvedRequests, resolvedRequests } = useMemo(() => {
    const unresolved: CareStateRequestT[] = [];
    const resolved: CareStateRequestT[] = [];
    for (const request of careState?.requests ?? []) {
      if (isUnresolved(request)) {
        unresolved.push(request);
      } else {
        resolved.push(request);
      }
    }
    return { unresolvedRequests: unresolved, resolvedRequests: resolved };
  }, [careState]);

  const visibleRequests = filter === 'unresolved' ? unresolvedRequests : resolvedRequests;
  const unresolvedCount = unresolvedRequests.length;

  const actionsByRequest = useMemo(() => groupActionsByRequest(actions), [actions]);
  const uncategorizedActions = useMemo<EnrichedProposedAction[]>(
    () => actions.filter((item) => item.action.link.type !== 'request'),
    [actions]
  );

  const handleWrapUp = () => {
    sendText({ text: WRAP_UP_CHART_PROMPT, source: WRAP_UP_CHART_PROMPT_SOURCE });
  };

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <h2 className="min-w-0 truncate text-lg font-medium leading-tight tracking-tight">
            <span className="text-muted-foreground">{displayName} is presenting with </span>
            <span className="text-foreground">
              {pluralize(unresolvedCount, 'unresolved request', 'unresolved requests')}
            </span>
          </h2>
          <Tabs value={filter} onValueChange={(next) => setFilter(next as RequestFilter)}>
            <TabsList>
              <TabsTrigger value="unresolved">Unresolved</TabsTrigger>
              <TabsTrigger value="resolved">Resolved</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        <Button
          type="button"
          radius="pill"
          size="chip"
          disabled={unresolvedCount === 0}
          onClick={handleWrapUp}
          className="gap-1.5 bg-foreground text-sm text-background hover:bg-foreground/90"
        >
          <CheckCheck className="size-5" aria-hidden="true" />
          <span>Update and close chart</span>
        </Button>
      </header>

      <CortexPromptStrip />

      <AgentTweakableArea prefix="request">
        <div className="flex flex-col gap-3">
          {visibleRequests.map((request) => (
            <RequestListCard
              key={request.id}
              request={request}
              actions={actionsByRequest.get(request.id) ?? []}
            />
          ))}
        </div>
      </AgentTweakableArea>

      {uncategorizedActions.length > 0 && (
        <Card tone="sunken" as="section" aria-label="Uncategorized suggested actions">
          <h3 className="text-sm font-medium text-foreground">Uncategorized suggested actions</h3>
          <ProposedActionList items={uncategorizedActions} />
        </Card>
      )}
    </div>
  );
}
